import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import { Button, Grid, Header, Message } from 'semantic-ui-react';
import { FormikInput, FormikTextArea, FormikSelect, FormikField } from '../../../pages/subPages/voiceActor/FormComponents';
import { FormChangeDetector } from '../../../bookWorksheet/components/common';
import * as Yup from 'yup';
import '../../styles/voiceActorWorksheet.css';

// Portfolio status options
const PORTFOLIO_STATUS_OPTIONS = [
  { key: 'active', text: 'Active', value: 'active' },
  { key: 'inactive', text: 'Inactive', value: 'inactive' },
  { key: 'archived', text: 'Archived', value: 'archived' },
  { key: 'draft', text: 'Draft', value: 'draft' }
];

// Portfolio usage options
const PORTFOLIO_USAGE_OPTIONS = [
  { key: 'commercial', text: 'Commercial', value: 'commercial' },
  { key: 'personal', text: 'Personal', value: 'personal' },
  { key: 'demo', text: 'Demo Reel', value: 'demo' },
  { key: 'audition', text: 'Audition', value: 'audition' }
];

// Validation schema for portfolio
const portfolioValidationSchema = Yup.object().shape({
  name: Yup.string().required('Portfolio name is required'),
  description: Yup.string().required('Description is required'),
  status: Yup.string().required('Status is required'),
  usage: Yup.string().required('Usage type is required')
});

/**
 * PortfolioForm component
 * Form for creating and editing portfolios
 * @param {Object} props - Component props
 * @param {Function} props.onSave - Callback when the form is saved
 * @param {Function} props.onDelete - Callback when delete is clicked
 * @param {Object} props.selectedPortfolio - The selected portfolio data
 * @param {string} props.portfolioId - The selected portfolio ID
 * @param {boolean} props.isEditing - Whether we're in editing mode
 * @returns {JSX.Element} PortfolioForm component
 */
const PortfolioForm = ({ onSave, onDelete, selectedPortfolio, portfolioId, isEditing }) => {
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [formChanged, setFormChanged] = useState(false);
  const formikRef = useRef();

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Form submission handler
  const handleSubmit = async (values) => {
    setFormLoading(true);
    setErrorMessage(null);
    setSuccessMessage('');
    console.log('[PORTFOLIO_FORM] Form values before submission:', values);

    try {
      if (isEditing && portfolioId) {
        // Update existing portfolio
        console.log('[PORTFOLIO_FORM] Updating portfolio:', portfolioId);
        await onSave({
          portfolioId,
          data: values
        });
        setSuccessMessage('Portfolio updated successfully!');
      } else {
        // Create new portfolio
        console.log('[PORTFOLIO_FORM] Creating new portfolio');
        await onSave({
          data: values
        });
        setSuccessMessage('Portfolio created successfully!');
      }

      setFormChanged(false);
    } catch (err) {
      console.error('[PORTFOLIO_FORM] Error saving portfolio:', err);
      setErrorMessage(err.message || 'An error occurred while saving the portfolio');
    } finally {
      setFormLoading(false);
    }
  };

  // Get initial values for the form
  const getFormValues = useCallback((data) => {
    return {
      name: data?.name || '',
      description: data?.description || '',
      status: data?.status || 'active',
      usage: data?.usage || 'commercial',
      client: data?.client || ''
    };
  }, []);

  // Check if we have portfolio data
  const hasPortfolioData = !!selectedPortfolio;

  const formReset = useCallback((resetData) => {
    if (formikRef.current) {
      console.log('[PORTFOLIO_FORM] Resetting form with data:', resetData);
      formikRef.current.resetForm({
        values: getFormValues(resetData)
      });
      setFormChanged(false);
    }
  }, [getFormValues]);

  return (
    <div className="va-portfolio-form">
      <Header as="h3">
        {isEditing ? 'Edit Portfolio' : 'Create New Portfolio'}
      </Header>

      {(!portfolioId || hasPortfolioData) && (
        <Formik
          innerRef={formikRef}
          initialValues={getFormValues(selectedPortfolio)}
          validationSchema={portfolioValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, isValid, dirty }) => (
            <Form>
              <FormChangeDetector
                onChange={setFormChanged}
                initialValues={getFormValues(selectedPortfolio)}
              />

              <Grid divided={false}>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <FormikField
                      name="name"
                      component={FormikInput}
                      label="Portfolio Name*"
                      placeholder="Enter portfolio name"
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <FormikField
                      name="description"
                      component={FormikTextArea}
                      label="Description*"
                      placeholder="Enter portfolio description"
                      rows={4}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={8}>
                    <FormikField
                      name="status"
                      component={FormikSelect}
                      label="Status*"
                      options={PORTFOLIO_STATUS_OPTIONS}
                      fluid
                      placeholder="Select status"
                    />
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <FormikField
                      name="usage"
                      component={FormikSelect}
                      label="Usage Type*"
                      options={PORTFOLIO_USAGE_OPTIONS}
                      fluid
                      placeholder="Select usage type"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <FormikField
                      name="client"
                      component={FormikInput}
                      label="Client (Optional)"
                      placeholder="Enter client name"
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                {/* Status messages */}
                {(successMessage || errorMessage) && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      {errorMessage && (
                        <Message negative>
                          <Message.Header>Error</Message.Header>
                          <p>{errorMessage}</p>
                        </Message>
                      )}

                      {successMessage && (
                        <Message positive>
                          <Message.Header>Success</Message.Header>
                          <p>{successMessage}</p>
                        </Message>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                )}

                {/* Form buttons */}
                <Grid.Row>
                  <Grid.Column width={16}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Button
                        primary
                        type="submit"
                        loading={formLoading}
                        disabled={!formChanged || !isValid}
                        onClick={() => handleSubmit(formikRef.current.values)}
                      >
                        {isEditing ? 'Update Portfolio' : 'Create Portfolio'}
                      </Button>

                      {formChanged && (
                        <Button
                          type="button"
                          onClick={() => formReset(selectedPortfolio)}
                        >
                          Cancel Changes
                        </Button>
                      )}

                      {isEditing && onDelete && (
                        <Button
                          negative
                          type="button"
                          onClick={() => onDelete(selectedPortfolio)}
                        >
                          Delete Portfolio
                        </Button>
                      )}
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default PortfolioForm;
